import React from 'react';
import { createPortal } from 'react-dom';
import { X, Share, PlusSquare, Bell, Smartphone, Settings } from 'lucide-react';
import { isIOS, isAndroid } from '../utils/pwaInstall';

const SERIF = { fontFamily: "'Playfair Display', serif" };

const Step = ({ n, icon: Icon, title, body }) => (
  <li className="flex gap-3">
    <span className="w-7 h-7 rounded-full bg-[#D6AD60] text-[#0f1e33] text-xs font-bold flex items-center justify-center shrink-0">{n}</span>
    <div className="min-w-0">
      <p className="text-sm font-semibold text-[#0f1e33] inline-flex items-center gap-1.5"><Icon className="w-4 h-4 text-[#a88a4c]" /> {title}</p>
      <p className="text-xs text-[#2d4a70] leading-relaxed mt-0.5">{body}</p>
    </div>
  </li>
);

// Step-by-step help for turning on notifications on iPhone / Android, incl. the "blocked" state.
export const NotificationHelpSheet = ({ open, onClose, mode = 'install' }) => {
  if (!open) return null;
  const ios = isIOS();
  const android = isAndroid();
  const title = mode === 'blocked' ? 'Notifications are blocked' : ios ? 'Add Reflections to your Home Screen' : 'Turn on notifications';
  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center" data-testid="notification-help-sheet">
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-black/55 backdrop-blur-sm" />
      <div className="relative w-full sm:max-w-md bg-[#FFFCF3] rounded-t-3xl sm:rounded-3xl p-5 pb-8 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#a88a4c]">{ios ? 'iPhone & iPad' : android ? 'Android' : 'Notifications'}</p>
            <h3 className="text-xl text-[#0f1e33] mt-1" style={SERIF} data-testid="notification-help-title">{title}</h3>
          </div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-[#E8DDCB] flex items-center justify-center" data-testid="notification-help-close"><X className="w-4 h-4 text-[#0f1e33]" /></button>
        </div>

        {mode === 'blocked' ? (
          <ol className="mt-4 space-y-3">
            {ios ? (
              <>
                <Step n={1} icon={Settings} title="Open Settings on your iPhone" body="Scroll to Notifications, then find “Reflections” in the app list (it appears once the app is on your Home Screen)." />
                <Step n={2} icon={Bell} title="Allow Notifications" body="Turn on Allow Notifications, then come back here and tap Turn on notifications again." />
              </>
            ) : (
              <>
                <Step n={1} icon={Settings} title="Open your browser's site settings" body="In Chrome: tap the lock/tune icon next to the address → Permissions → Notifications → Allow. Or Android Settings → Apps → Chrome → Notifications." />
                <Step n={2} icon={Bell} title="Come back and tap Turn on" body="Reload this page, then tap Turn on notifications again." />
              </>
            )}
          </ol>
        ) : ios ? (
          <>
            <p className="text-xs text-[#2d4a70] mt-2">Apple only allows notifications from web apps that live on your Home Screen (iOS 16.4 or newer). It takes about 20 seconds:</p>
            <ol className="mt-4 space-y-3">
              <Step n={1} icon={Share} title="Tap the Share button in Safari" body="It's the square with an arrow at the bottom of the screen. If you opened this link inside another app (Instagram, Facebook, Messages), tap “Open in Safari” first." />
              <Step n={2} icon={PlusSquare} title="Choose “Add to Home Screen”" body="Scroll the share sheet until you see it, then tap Add in the top-right corner." />
              <Step n={3} icon={Smartphone} title="Open Reflections from your Home Screen" body="Close Safari and tap the new Reflections icon." />
              <Step n={4} icon={Bell} title="Profile → Turn on notifications" body="Tap the gold button, then tap Allow when your iPhone asks." />
            </ol>
          </>
        ) : (
          <ol className="mt-4 space-y-3">
            <Step n={1} icon={Bell} title="Tap Turn on notifications" body="Your phone will ask for permission — tap Allow." />
            <Step n={2} icon={PlusSquare} title="Optional: install the app" body="In Chrome tap ⋮ → “Add to Home screen” (or “Install app”) so Reflections opens like any other app. Notifications work either way." />
            <Step n={3} icon={Smartphone} title="If nothing happens" body="Make sure you're in Chrome, Samsung Internet, Edge or Firefox — not a browser inside another app — and that Do Not Disturb is off." />
          </ol>
        )}
        <button type="button" onClick={onClose} className="mt-6 w-full h-12 rounded-full bg-[#0f1e33] text-[#f9f4ea] text-xs font-semibold uppercase tracking-[0.2em]" data-testid="notification-help-done">Got it</button>
      </div>
    </div>,
    document.body,
  );
};

export default NotificationHelpSheet;
